import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Group,
  Indicator,
  SimpleGrid,
  Stack,
  Text,
  useMantineTheme,
  Paper,
  Modal,
  LoadingOverlay,
  Divider,
  Badge,
  Tooltip,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconDownload, IconEdit, IconPrinter } from "@tabler/icons-react";
import InfoIcon from "../../../assets/InfoIcon.svg";
import { CheckboxGroup, RadioGroup } from "../CheckboxGroup";
import HomeIcon from "../../../assets/HomeIcon.svg";
import NotificationBanner from "../NotificationBanner";
import SignatureFooter from "../SignatureFooter";
import ClinicalDocumentation from "../ClinicalDocumentation";
import arcLogo from "../../../assets/ARCPlusPrimary.svg";
import supportIcon from "../../../assets/SupportIcon.svg";
import { InfoPanel } from "../infopanel/infopanel";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useColors from "../../../theme/useColors";
import {
  DATE_FORMAT,
  DUMMY_TEXT,
  OTHER_OPTION_VALUE,
} from "../../../constants/app-constant";
import { formatDate, formatTimeWithZoneAbbreviation } from "../../../utils";
import SignatureInputDialog from "../SignatureInputDialog";
import {
  buildSections,
  extractDiagnosisOptions,
  otherFieldCheck,
  parseOptions,
} from "../../../utils/query-detail-utils";
import {
  getHomeScreenSignatureDefault,
  getHomeScreenUserName,
} from "../../../utils/provider-display-utils";
import { useFhirClient } from "../../../hooks/useFhirClient";
import { RouteConstants } from "../../../constants/route-constant";
import ResetModal from "./ResetModal";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setProvider } from "../../../store/providerReducer";
import { danger, success, warning } from "../../../constants/color-constants";
import {
  fetchPdfUrlQuery,
  fetchPdfUrlWithRetry,
  useConversationList,
  usePostMDMDocumentReference,
  usePostNewConversation,
  useProviderDetails,
  useQueryDetailAnswers,
  useQueryDetailsData,
  useUpdateConversationStatus,
  useUpdateQueryDetail,
} from "../../../hooks/queries";

const bulwarkLogo = "/bulwark_logo-new.png";

interface PdfResponse {
  blobData: Blob;
  filename: string;
  contentType: string;
  size: number;
}

const QueryDetails: React.FC = () => {
  const colors = useColors();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { query_id } = useParams();
  const { state } = useLocation();

  const [queryData] = useState<any>(state?.queryData || {});
  const [isEdit, setIsEdit] = useState<boolean>(state?.editMode);
  const previousRoute = state?.from;
  const provider = useAppSelector((state) => state?.provider?.provider);
  const { patient, practitioner, encounter, mrn } = useAppSelector(
    (state) => state?.fhir
  );
  const providerNPI = useAppSelector((state) => state?.provider?.providerNPI);
  const client = useFhirClient();

  const isMobile = useMediaQuery(`(max-width: ${"760px"})`);
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const contentRef = useRef<HTMLDivElement>(null);
  const localSignatureRef = useRef(false);
  const [isBackModalOpen, setBackModalOpen] = useState(false);
  const [openResetModal, setOpenResetModal] = useState<boolean>(false);

  // Form state variables
  const [diagnosisAcuity, setDiagnosisAcuity] = useState<string[]>([]);
  const [otherAcuityText, setOtherAcuityText] = useState<string>("");
  const [diagnosisType, setDiagnosisType] = useState<string[]>([]);
  const [otherTypeText, setOtherTypeText] = useState<string>("");
  const [diagnosisPOA, setDiagnosisPOA] = useState<string[]>([]);
  const [otherPOAText, setOtherPOAText] = useState<string>("");
  const [diagnosisQ3, setDiagnosisQ3] = useState<string[]>([]);
  const [otherQ3Text, setOtherQ3Text] = useState<string>("");
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);
  const [signatureInfo, setSignatureInfo] = useState<any>();
  const [sectionData, setSectionData] = useState<any[]>([]);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [conversationData, setConversationData] = useState<any>();
  const [, setHasInitialValues] = useState<boolean>(false);
  const [formTouched, setFormTouched] = useState<boolean>(false);

  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [signedValue, setSignedValue] = useState("");
  const [queryDetails, setQueryDetails] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  // PDF/Print related states
  const [downloading, setDownloading] = useState<boolean>(false);
  const [printing, setPrinting] = useState<boolean>(false);

  // FHIR related states
  const [documentRefCreating, setDocumentRefCreating] =
    useState<boolean>(false);
  const [, setDocumentRefProgress] = useState<string>("");

  const providerInfo = provider?.provider_info;

  const { providerDisplayName, providerSignatureDefault } = useMemo(
    () => ({
      providerDisplayName: getHomeScreenUserName(
        providerInfo,
        client,
        practitioner
      ),
      providerSignatureDefault: getHomeScreenSignatureDefault(
        providerInfo,
        client,
        practitioner
      ),
    }),
    [providerInfo, client, practitioner]
  );

  const { data: providerDetailsResponse } = useProviderDetails(providerNPI);

  useEffect(() => {
    if (providerDetailsResponse?.data?.data) {
      dispatch(setProvider(providerDetailsResponse.data.data));
    }
  }, [dispatch, providerDetailsResponse]);

  const queryStatus = queryDetails?.QUERY_STATUS_ID;

  const updateQueryDetailMutation = useUpdateQueryDetail();
  const postNewConversationMutation = usePostNewConversation();
  const updateConversationStatusMutation = useUpdateConversationStatus();
  const postMDMDocumentReferenceMutation = usePostMDMDocumentReference();

  const activeQueryId = query_id ?? "";

  const queryDetailsParams = useMemo(
    () => ({
      query_id: activeQueryId,
      patient_id: queryData?.patientId,
    }),
    [activeQueryId, queryData?.patientId]
  );

  const queryAnswersParams = useMemo(
    () => ({
      query_id: activeQueryId,
    }),
    [activeQueryId]
  );

  const {
    data: queryDetailsResponse,
    isLoading: queryDetailsLoading,
    refetch: refetchQueryDetails,
  } = useQueryDetailsData(queryDetailsParams, Boolean(activeQueryId));

  const {
    data: queryAnswersResponse,
    refetch: refetchQueryDetailAnswers,
  } = useQueryDetailAnswers(queryAnswersParams, Boolean(activeQueryId));

  const conversationListParams = useMemo(
    () => ({
      provider_npi: providerNPI,
      query_id: queryDetails?.QUERY_ID || activeQueryId,
    }),
    [providerNPI, queryDetails?.QUERY_ID, activeQueryId]
  );

  const { data: conversationListResponse } = useConversationList(
    conversationListParams,
    {
      enabled: Boolean(providerNPI && (queryDetails?.QUERY_ID || activeQueryId)),
    }
  );

  useEffect(() => {
    setLoading(queryDetailsLoading);
  }, [queryDetailsLoading]);

  useEffect(() => {
    if (queryDetailsResponse?.status !== 200) return;

    const queryDetailData = queryDetailsResponse.data?.data;
    setQueryDetails(queryDetailData || {});
    setSectionData(
      buildSections({
        CLINICAL_INDICATORS: queryDetailData?.CLINICAL_INDICATORS,
        SIGN_LAB: queryDetailData?.SIGN_LAB || "Signs and Symptoms/ Vitals",
        LABEL_LAB: queryDetailData?.LABEL_LAB || "Lab/Imaging Findings",
        TREAT_LAB: queryDetailData?.TREAT_LAB || "Treatment/Management",
      })
    );
    setIsPreviewMode(
      isEdit
        ? false
        : [2, 3].includes(Number(queryDetailData?.QUERY_STATUS_ID))
    );
  }, [isEdit, queryDetailsResponse]);

  useEffect(() => {
    if (conversationListResponse?.status === 200) {
      setConversationData(conversationListResponse.data?.data?.[0]);
    }
  }, [conversationListResponse]);

  useEffect(() => {
    if (queryAnswersResponse?.status !== 200) return;

    const answers = queryAnswersResponse.data?.data;
    if (answers?.QUERY_ID) {
      setHasInitialValues(true);
    }
    setDiagnosisAcuity(
      extractDiagnosisOptions(answers?.SELECTED_OPTIONS?.ACUTE_OF_DIAGNOSIS)
        .selectedOptions
    );
    setOtherAcuityText(
      extractDiagnosisOptions(answers?.SELECTED_OPTIONS?.ACUTE_OF_DIAGNOSIS)
        .otherText || ""
    );
    setDiagnosisType(
      extractDiagnosisOptions(answers?.SELECTED_OPTIONS?.TYPE_OF_DIAGNOSIS)
        .selectedOptions
    );
    setOtherTypeText(
      extractDiagnosisOptions(answers?.SELECTED_OPTIONS?.TYPE_OF_DIAGNOSIS)
        .otherText || ""
    );
    setDiagnosisPOA(
      extractDiagnosisOptions(answers?.SELECTED_OPTIONS?.POA_OF_DIAGNOSIS)
        .selectedOptions
    );
    setOtherPOAText(
      extractDiagnosisOptions(answers?.SELECTED_OPTIONS?.POA_OF_DIAGNOSIS)
        .otherText || ""
    );
    setDiagnosisQ3(
      extractDiagnosisOptions(answers?.SELECTED_OPTIONS?.Q3_OF_DIAGNOSIS)
        .selectedOptions
    );
    setOtherQ3Text(
      extractDiagnosisOptions(answers?.SELECTED_OPTIONS?.Q3_OF_DIAGNOSIS)
        .otherText || ""
    );

    if (!localSignatureRef.current) {
      setSignatureFile(answers?.SIGNATURE_IMAGE ?? null);
    }

    if (answers?.CREATED_BY || answers?.UPDATED_DATE) {
      setSignatureInfo({
        ...answers?.CREATED_BY,
        CREATED_DATE: answers?.CREATED_DATE,
        UPDATED_DATE: answers?.UPDATED_DATE,
      });
    }
  }, [queryAnswersResponse]);

  const parsedAcuity = parseOptions(queryDetails?.ACUTE_OF_DIAGNOSIS);
  const parsedType = parseOptions(queryDetails?.TYPE_OF_DIAGNOSIS);
  const parsedPOA = parseOptions(queryDetails?.POA_REPORT_OPTION);
  const parsedQ3 = parseOptions(queryDetails?.Q3_REPORT_DIAG);

  const createDocumentReferenceLatest = async (submittedQueryId: string) => {
    try {
      setDocumentRefProgress("Generating query PDF...");
      const pdfResponse = await fetchPdfUrlWithRetry(submittedQueryId);

      if (!pdfResponse?.blobData) {
        throw new Error(
          "Unable to generate the query PDF. The query was saved, but uploading to Epic failed."
        );
      }
      if (!patient) {
        throw new Error("Patient not found");
      }
      if (pdfResponse.size === 0) {
        throw new Error("Received empty PDF file");
      }

      // Convert blobData to Blob if it's a string (same logic as handlePrint)
      const blobData = pdfResponse.blobData;
      const contentType = pdfResponse.contentType || "application/pdf";

      let actualBlob: Blob;

      if (blobData instanceof Blob) {
        actualBlob = blobData;
      } else if (typeof blobData === "string") {
        try {
          const stringData = blobData as string;
          const base64Data = stringData.includes(",")
            ? stringData.split(",")[1]
            : stringData;

          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          actualBlob = new Blob([bytes], { type: contentType });
        } catch (error) {
          console.error(
            "Failed to convert base64 to blob for document reference:",
            error
          );
          throw new Error("Failed to process PDF data");
        }
      } else {
        throw new Error("Invalid PDF data format");
      }

      if (actualBlob.size === 0) {
        throw new Error("Received empty PDF file");
      }

      // Create FormData
      const formData = new FormData();

      // Append PDF file with filename from response
      const filename =
        pdfResponse.filename ||
        `${queryDetails?.PATIENT_NAME}_query_${queryData?.id}.pdf`;

      formData.append("file", actualBlob, filename);

      // Append JSON stringified data to match backend expectations
      formData.append("patient", JSON.stringify(patient));

      if (practitioner) {
        formData.append("practitioner", JSON.stringify(practitioner));
      }
      if (encounter) {
        formData.append("visitId", JSON.stringify(queryDetails?.VISIT_ID));
      }
      if (client?.fhirBaseUrl) {
        formData.append("fhirBaseUrl", client?.fhirBaseUrl);
      }
      if (client?.state?.tokenUri) {
        formData.append("tokenUri", client?.state?.tokenUri);
      }
      if (mrn) {
        formData.append("mrn", mrn);
      }

      // Make API call
      await postMDMDocumentReferenceMutation.mutateAsync(formData);
    } catch (error) {
      console.error("Error creating document reference:", error);
      throw error;
    }
  };

  // PDF utility functions
  const downloadPdf = async (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const isEpicHyperspace = (): boolean => {
    return !!(
      (window as any).Epic ||
      (window as any).EpicWeb ||
      window.self !== window.top ||
      window.location.href.includes("epic") ||
      window.location.href.includes("hyperspace")
    );
  };

  const openPdfInExternalWindow = (blob: Blob, filename?: string) => {
    const url = URL.createObjectURL(blob);

    try {
      if (isEpicHyperspace()) {
        try {
          if (
            (window as any).Epic?.Clinical?.Informatics?.Web?.OpenExternalWindow
          ) {
            (window as any).Epic.Clinical.Informatics.Web.OpenExternalWindow(
              url
            );
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            return;
          }
        } catch (error) {
          console.warn(
            "Epic OpenExternalWindow method failed, fallback to window.open:",
            error
          );
        }
      }

      const externalWindow = window.open(
        url,
        "_blank",
        "width=1024,height=768,scrollbars=yes,resizable=yes"
      );

      if (!externalWindow) {
        // Pop-up blocked → just alert or notify user
        console.error("Pop-up blocked or failed to open external window");
      }

      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.error("Failed to open PDF in external window:", error);
      // ❌ Removed downloadPdf fallback
      alert("Failed to open PDF. Please try again.");
      URL.revokeObjectURL(url);
    }
  };

  const fetchPdfUrlFromArcPlus = async (
    id: string
  ): Promise<PdfResponse | null> => {
    try {
      const response = await fetchPdfUrlQuery(id);
      return response?.data || null;
    } catch (error) {
      console.error("Failed to fetch PDF URL:", error);
      return null;
    }
  };

  const handleDownload = async () => {
    if (!query_id) return;

    try {
      setDownloading(true);

      const response = await fetchPdfUrlFromArcPlus(query_id);

      if (!response) {
        console.error("No PDF response received for print");
        return;
      }

      const blobData = response?.blobData;
      const filename =
        response?.filename ||
        `${queryDetails?.PATIENT_NAME}_query_${query_id}.pdf`;
      const contentType = response?.contentType || "application/pdf";

      let actualBlob: Blob;

      if (blobData instanceof Blob) {
        actualBlob = blobData;
      } else if (typeof blobData === "string") {
        try {
          const stringData = blobData as string;
          const base64Data = stringData.includes(",")
            ? stringData.split(",")[1]
            : stringData;

          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          actualBlob = new Blob([bytes], { type: contentType });
        } catch (error) {
          console.error("Failed to convert base64 to blob for print:", error);
          return;
        }
      } else {
        console.error("Invalid PDF data format for print");
        return;
      }

      if (actualBlob.size === 0) {
        console.error("Received empty PDF file for print");
        return;
      }
      await downloadPdf(actualBlob, filename);
      openPdfInExternalWindow(actualBlob, filename);
    } catch (error) {
      console.error("Error handling PDF print:", error);
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = async () => {
    if (!query_id) return;

    try {
      setPrinting(true);

      const response = await fetchPdfUrlFromArcPlus(query_id);

      if (!response) {
        console.error("No PDF response received for print");
        return;
      }

      const blobData = response?.blobData;
      const filename =
        response?.filename ||
        `${queryDetails?.PATIENT_NAME}_query_${query_id}.pdf`;
      const contentType = response?.contentType || "application/pdf";

      let actualBlob: Blob;

      if (blobData instanceof Blob) {
        actualBlob = blobData;
      } else if (typeof blobData === "string") {
        try {
          const stringData = blobData as string;
          const base64Data = stringData.includes(",")
            ? stringData.split(",")[1]
            : stringData;

          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          actualBlob = new Blob([bytes], { type: contentType });
        } catch (error) {
          console.error("Failed to convert base64 to blob for print:", error);
          return;
        }
      } else {
        console.error("Invalid PDF data format for print");
        return;
      }

      if (actualBlob.size === 0) {
        console.error("Received empty PDF file for print");
        return;
      }

      openPdfInExternalWindow(actualBlob, filename);
    } catch (error) {
      console.error("Error handling PDF print:", error);
    } finally {
      setPrinting(false);
    }
  };

  // Form validation

  const isFieldValid = (
    selectedOptions: string[],
    otherText: string,
    parsedField?: { options?: string[] }
  ): boolean => {
    if (!parsedField?.options?.length) {
      return true;
    }
    if (
      selectedOptions?.includes(OTHER_OPTION_VALUE) &&
      otherText?.trim() === ""
    ) {
      return false;
    }
    return selectedOptions?.length > 0;
  };

  const isAcuityValid = (): boolean =>
    isFieldValid(diagnosisAcuity, otherAcuityText, parsedAcuity);

  const isTypeValid = (): boolean =>
    isFieldValid(diagnosisType, otherTypeText, parsedType);

  const isPOAValid = (): boolean =>
    isFieldValid(diagnosisPOA, otherPOAText, parsedPOA);

  const isQ3Valid = (): boolean =>
    isFieldValid(diagnosisQ3, otherQ3Text, parsedQ3);

  const isFormValid = (): boolean => {
    const acuityValid = isAcuityValid();
    const typeValid = isTypeValid();
    const poaValid = isPOAValid();
    const q3Valid = isQ3Valid();

    return acuityValid && typeValid && poaValid && q3Valid;
  };

  const handleAcuityChange = (selectedOptions: string[]) => {
    setDiagnosisAcuity(selectedOptions);
    setFormTouched(true);
  };

  const handleOtherAcuityChange = (value: string) => {
    setOtherAcuityText(value);
    setFormTouched(true);
  };

  const handleOtherTypeChange = (value: string) => {
    setOtherTypeText(value);
    setFormTouched(true);
  };

  const handleOtherPOAChange = (value: string) => {
    setOtherPOAText(value);
    setFormTouched(true);
  };

  const handleOtherQ3Change = (value: string) => {
    setOtherQ3Text(value);
    setFormTouched(true);
  };

  const handleDiagnosisType = (selectedOption: string[]) => {
    setDiagnosisType(selectedOption);
    setFormTouched(true);
  };

  const handleDiagnosisPOA = (selectedOption: string[]) => {
    setDiagnosisPOA(selectedOption);
    setFormTouched(true);
  };

  const handleDiagnosisQ3 = (selectedOption: string[]) => {
    setDiagnosisQ3(selectedOption);
    setFormTouched(true);
  };

  const handleSubmitSignature = (signature: string, file: File) => {
    localSignatureRef.current = true;
    setSignedValue(signature);
    setSignatureFile(file);
    setIsSignatureModalOpen(false);
    setFormTouched(true);
  };

  const documentationData = {
    sections: sectionData,
    clinicalNotes: {
      title: `${
        queryDetails?.LABEL_DOC ||
        "Relevant clinical documentation references are :"
      }`,

      items: queryDetails?.DOC_TYPE_INDICATOR
        ? queryDetails?.DOC_TYPE_INDICATOR?.split(";")
        : [DUMMY_TEXT.DOC_TYPE_INDICATOR],
    },
  };
  const refreshQueryData = async (newQueryId = "") => {
    if (newQueryId) {
      await Promise.all([
        refetchQueryDetails(),
        refetchQueryDetailAnswers(),
      ]);
      return;
    }
    await Promise.all([refetchQueryDetails(), refetchQueryDetailAnswers()]);
  };

  const submitQueryDetails = async (statusId: number | string) => {
    statusId === 2 ? setSubmitLoading(true) : setSaveLoading(true);

    const payload = {
      query_id: queryDetails?.QUERY_ID,
      remarks: "",
      status_id: statusId,
      SELECTED_ACUTE_OF_DIAGNOSIS: otherFieldCheck(
        diagnosisAcuity,
        otherAcuityText
      ),
      SELECTED_TYPE_OF_DIAGNOSIS: otherFieldCheck(diagnosisType, otherTypeText),
      SELECTED_POA_OF_DIAGNOSIS: otherFieldCheck(diagnosisPOA, otherPOAText),
      SELECTED_Q3_OF_DIAGNOSIS: otherFieldCheck(diagnosisQ3, otherQ3Text),
      ...(signedValue && signatureFile instanceof File
        ? { signature_image: signatureFile }
        : {}),
      provider_npi: providerNPI,
    };

    const params = {
      provider_npi: providerNPI,
    };

    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (key === "signature_image" && value instanceof File) {
        formData.append(key, value, value.name);
      } else if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });

    let newQueryId = "";
    try {
      const response = await updateQueryDetailMutation.mutateAsync({
        body: formData,
        params,
      });

      newQueryId = response?.data?.query_id;
      return newQueryId;
    } catch (error) {
      console.error("PUT API failed:", error);
      throw error;
    } finally {
      await refreshQueryData(newQueryId);
      setSubmitLoading(false);
      setSaveLoading(false);
    }
  };

  // Updated handleSubmit to include DocumentReference creation
  const handleSubmit = async (status: string | number) => {
    let newQueryId: string | undefined;

    try {
      newQueryId = await submitQueryDetails(status);

      if (!newQueryId) {
        throw new Error("Failed to save query details");
      }

      if (status === 2 && conversationData) {
        await updateConversationStatusMutation.mutateAsync({
          conversation_id: conversationData?.CONVERSATION_ID,
          provider_npi: providerNPI,
          status: "CLOSED",
        });
      }

      setIsEdit(false);
      setFormTouched(false);
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
      navigate(
        RouteConstants.ROUTE_QUERY_DETAILS.replace(
          ":query_id",
          newQueryId ?? ""
        ),
        {
          state: {
            queryData: {
              id: newQueryId,
              patientId: queryDetails?.PATIENT_ID,
            },
            from: previousRoute,
          },
        }
      );
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      return;
    }

    if (status === 2 && newQueryId) {
      try {
        setDocumentRefCreating(true);
        setDocumentRefProgress("Creating document reference...");
        await createDocumentReferenceLatest(newQueryId);
        setDocumentRefProgress("Document reference created successfully");
        console.log("Document reference creation completed");
      } catch (error) {
        console.error("Failed to create document reference:", error);
        setDocumentRefProgress("");
        alert(
          "Your query was submitted, but uploading the PDF to Epic failed. Please try printing the query PDF manually or contact support if this continues."
        );
      } finally {
        setDocumentRefCreating(false);
      }
    }
  };

  const handleNavigateToMessage = async () => {
    try {
      const enrichedConversationData = conversationData
        ? {
            ...conversationData,
            QUERY_ID: conversationData?.QUERY_ID || queryDetails?.QUERY_ID,
            PATIENT_ID:
              conversationData?.PATIENT_ID || queryDetails?.PATIENT_ID,
          }
        : null;

      if (enrichedConversationData?.CONVERSATION_ID) {
        navigate(
          RouteConstants?.ROUTE_MESSAGE_DETAILS.replace(
            ":conversation_id",
            enrichedConversationData.CONVERSATION_ID
          ),
          {
            state: {
              conversationData: enrichedConversationData,
              from: previousRoute,
            },
          }
        );
      } else {
        const response = await postNewConversationMutation.mutateAsync({
          provider_npi: providerNPI,
          query_id: queryDetails?.QUERY_ID,
          patient_id: queryDetails?.PATIENT_ID,
        });
        if (response?.data?.data?.conversation_id)
          navigate(
            RouteConstants.ROUTE_MESSAGE_DETAILS.replace(
              ":conversation_id",
              response?.data?.data?.conversation_id
            ),
            {
              state: {
                conversationData: {
                  QUERY_ID: queryDetails?.QUERY_ID,
                  PATIENT_ID: queryDetails?.PATIENT_ID,
                  STATUS: "OPEN",
                  CONVERSATION_ID: response?.data?.data?.conversation_id,
                },
                from: previousRoute,
              },
            }
          );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleBack = () => {
    if (
      formTouched &&
      (diagnosisAcuity.length > 0 ||
        diagnosisPOA.length > 0 ||
        diagnosisPOA.length > 0)
    ) {
      setBackModalOpen(true);
    } else {
      navigate(previousRoute || "/");
    }
  };

  const handleReset = async () => {
    localSignatureRef.current = false;
    setDiagnosisAcuity([]);
    setOtherAcuityText("");
    setDiagnosisType([]);
    setOtherTypeText("");
    setDiagnosisPOA([]);
    setOtherPOAText("");
    setDiagnosisQ3([]);
    setOtherQ3Text("");
    setSignatureFile(null);
    setSignatureInfo(null);
    setFormTouched(false);
    setOpenResetModal(false);
    setSignedValue("");
  };

  const diagnosisGroup = [
    queryDetails?.LABEL_ACUITY &&
      parsedAcuity?.options?.length &&
      (queryDetails?.IS_Q1_MULTISELECT === "Y" ? (
        <CheckboxGroup
          key='diagnosis-acuity'
          title={queryDetails.LABEL_ACUITY}
          options={parsedAcuity.options}
          otherOption={parsedAcuity.hasOtherOption}
          required
          onChange={handleAcuityChange}
          onOtherChange={handleOtherAcuityChange}
          otherText={otherAcuityText}
          selectedOptions={diagnosisAcuity}
          isPreviewMode={isPreviewMode}
        />
      ) : (
        <RadioGroup
          key='diagnosis-acuity'
          title={queryDetails.LABEL_ACUITY}
          options={parsedAcuity.options}
          otherOption={parsedAcuity.hasOtherOption}
          required
          onChange={handleAcuityChange}
          onOtherChange={handleOtherAcuityChange}
          otherText={otherAcuityText}
          selectedOption={diagnosisAcuity}
          isPreviewMode={isPreviewMode}
        />
      )),

    queryDetails?.LABEL_TYPE &&
      parsedType?.options?.length &&
      (queryDetails?.IS_Q2_MULTISELECT === "Y" ? (
        <CheckboxGroup
          key='diagnosis-type'
          title={queryDetails.LABEL_TYPE}
          options={parsedType.options}
          otherOption={parsedType.hasOtherOption}
          otherText={otherTypeText}
          onOtherChange={handleOtherTypeChange}
          required={true}
          onChange={handleDiagnosisType}
          selectedOptions={diagnosisType}
          isPreviewMode={isPreviewMode}
        />
      ) : (
        <RadioGroup
          key='diagnosis-type'
          title={queryDetails.LABEL_TYPE}
          options={parsedType.options}
          otherOption={parsedType.hasOtherOption}
          otherText={otherTypeText}
          onOtherChange={handleOtherTypeChange}
          required={true}
          onChange={handleDiagnosisType}
          selectedOption={diagnosisType}
          isPreviewMode={isPreviewMode}
        />
      )),

    // Q3 or POA (only one or none will render)
    (() => {
      if (queryDetails?.LABEL_Q3 && parsedQ3?.options?.length) {
        return queryDetails.IS_Q3_MULTISELECT === "Y" ? (
          <CheckboxGroup
            key='diagnosis-q3'
            title={queryDetails.LABEL_Q3}
            options={parsedQ3.options}
            required={true}
            otherOption={parsedQ3.hasOtherOption}
            otherText={otherQ3Text}
            onOtherChange={handleOtherQ3Change}
            onChange={handleDiagnosisQ3}
            selectedOptions={diagnosisQ3}
            isPreviewMode={isPreviewMode}
          />
        ) : (
          <RadioGroup
            key='diagnosis-q3'
            title={queryDetails.LABEL_Q3}
            options={parsedQ3.options}
            required={true}
            otherOption={parsedQ3.hasOtherOption}
            otherText={otherQ3Text}
            onOtherChange={handleOtherQ3Change}
            onChange={handleDiagnosisQ3}
            selectedOption={diagnosisQ3}
            isPreviewMode={isPreviewMode}
          />
        );
      }

      if (queryDetails?.LABEL_POA && parsedPOA?.options?.length) {
        return queryDetails.IS_POA_MULTISELECT === "Y" ? (
          <CheckboxGroup
            key='diagnosis-poa'
            title={queryDetails.LABEL_POA}
            options={parsedPOA.options}
            required={true}
            otherOption={parsedPOA.hasOtherOption}
            otherText={otherPOAText}
            onOtherChange={handleOtherPOAChange}
            onChange={handleDiagnosisPOA}
            selectedOptions={diagnosisPOA}
            isPreviewMode={isPreviewMode}
          />
        ) : (
          <RadioGroup
            key='diagnosis-poa'
            title={queryDetails.LABEL_POA}
            options={parsedPOA.options}
            required={true}
            otherOption={parsedPOA.hasOtherOption}
            otherText={otherPOAText}
            onOtherChange={handleOtherPOAChange}
            onChange={handleDiagnosisPOA}
            selectedOption={diagnosisPOA}
            isPreviewMode={isPreviewMode}
          />
        );
      }

      return null;
    })(),
  ]?.filter(Boolean);

  const admission = formatDate(
    queryDetails?.ADMISSION_DATE,
    DATE_FORMAT.MM_DD_YYYY
  );
  const discharge = formatDate(
    queryDetails?.DISCHARGE_DATE,
    DATE_FORMAT.MM_DD_YYYY
  );
  const los = queryDetails?.LOS != null ? `${queryDetails.LOS} days` : "-";

  return (
    <Box>
      <LoadingOverlay
        visible={loading || documentRefCreating}
        zIndex={999}
        overlayProps={{ radius: "sm", blur: "2" }}
      />

      <Paper
        shadow='xs'
        // p={{ base: "xs", sm: "xs" }}
        pt={"2px"}
        style={{
          overflow: "hidden",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
        withBorder
      >
        {/* Header Section */}
        <Flex
          direction='row'
          align='center'
          justify='space-between'
          px='4px'
          pt={"8px"}
          wrap='nowrap'
          style={{ overflowX: "auto", whiteSpace: "nowrap" }}
        >
          {/* Left Logo */}
          <Box
            component='img'
            src={arcLogo}
            style={{
              height: isSmallScreen ? 18 : 20,
              width: "auto",
              flexShrink: 0,
            }}
          />

          {/* Center: Query Info + Buttons */}
          <Flex
            direction='row'
            justify='space-between'
            align='center'
            gap='xs'
            px={"xs"}
            style={{ flex: 1, minWidth: 0 }} // Allow it to shrink but not overflow
          >
            {/* Query Info */}
            <Group gap={isMobile ? "4px" : "xs"} style={{ flexWrap: "nowrap" }}>
              <Group
                gap={isMobile ? "4px" : "xs"}
                style={{ flexWrap: isMobile ? "wrap" : "nowrap" }}
              >
                <Text c='dimmed' size='12px' lh='12px' fw={400}>
                  Query ID:
                </Text>
                <Text
                  fw={500}
                  c={colors.gray[8]}
                  size='12px'
                  lh='12px'
                  style={{
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {queryDetails?.QUERY_ID
                    ? queryDetails?.QUERY_ID
                    : queryData?.id}
                </Text>

                {isPreviewMode &&
                  Number(queryDetails?.QUERY_STATUS_ID) === 3 && (
                    <Button
                      size={isMobile ? "compact-xs" : "xs"}
                      color={colors.primary[5]}
                      variant='outline'
                      ml={2}
                      mt={1}
                      onClick={() => setIsPreviewMode(false)}
                      style={{
                        fontWeight: 500,
                        fontSize: "10px",
                        transition: "none",
                        transform: "none",
                        "&:active": {
                          transform: "none",
                        },
                      }}
                    >
                      Go Back & Edit
                    </Button>
                  )}
              </Group>
            </Group>

            <Group
              gap={isMobile ? "4px" : "xs"}
              style={{
                flexWrap: isMobile ? "wrap" : "nowrap",
                flexGrow: 0.25,
                alignItems: "flex-start",
                justifyContent: "flex-start",
              }}
            >
              {queryDetails?.QUERY_STATUS_ID && (
                <Badge
                  color={
                    isPreviewMode && Number(queryDetails?.QUERY_STATUS_ID) === 2
                      ? success[6]
                      : isPreviewMode
                      ? warning[4]
                      : danger[4]
                  }
                  variant='light'
                  radius='sm'
                  size='xs'
                  style={{
                    border: `1px solid ${
                      isPreviewMode &&
                      Number(queryDetails?.QUERY_STATUS_ID) === 2
                        ? success[6]
                        : isPreviewMode
                        ? warning[4]
                        : danger[4]
                    }`,
                    height: isMobile ? "22px" : "30px",
                    marginTop: isMobile ? "1px" : "unset",
                    lineHeight: "22px",
                    padding: "0px 4px",
                    minWidth: "75px",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    fontSize: isMobile ? "10px" : "14px",
                  }}
                >
                  {isPreviewMode && Number(queryDetails?.QUERY_STATUS_ID) === 2
                    ? `Completed`
                    : isPreviewMode
                    ? `Query Preview`
                    : `Edit Mode`}
                </Badge>
              )}
            </Group>

            {/* Action Buttons */}
            <Group gap={isMobile ? "4px" : "xs"} style={{ flexWrap: "nowrap" }}>
              {!(
                isPreviewMode && Number(queryDetails?.QUERY_STATUS_ID) === 3
              ) && (
                <Button
                  variant='outline'
                  size={isMobile ? "compact-xs" : "xs"}
                  onClick={handleBack}
                  style={{
                    fontWeight: 500,
                    fontSize: "10px",
                    marginTop: isMobile ? "1.4px" : "unset",
                    transition: "none",
                    transform: "none",
                    "&:active": {
                      transform: "none",
                    },
                  }}
                  c={colors.gray[8]}
                  color={colors.primary[5]}
                >
                  Home
                  <Box
                    component='img'
                    src={HomeIcon}
                    ml={isMobile ? "4px" : "6px"}
                    h={isMobile ? "12px" : "16px"}
                    w={isMobile ? "12px" : "16px"}
                    mb={2}
                  />
                </Button>
              )}
              <Indicator
                color='red'
                label={
                  conversationData?.UNREAD_COUNT > 0
                    ? conversationData?.UNREAD_COUNT
                    : ""
                }
                disabled={conversationData?.UNREAD_COUNT > 0 ? false : true}
                size={isSmallScreen ? "18" : "20"}
                style={{
                  "--indicator-top": isSmallScreen ? "6px" : "6px",
                  "--indicator-right": isSmallScreen ? "0px" : "6px",
                }}
              >
                <Button
                  size={isMobile ? "compact-xs" : "xs"}
                  color={colors.primary[5]}
                  variant='outline'
                  onClick={handleNavigateToMessage}
                  style={{
                    fontSize: "10px",
                    alignItems: "center",
                    transition: "none",
                    transform: "none",
                    "&:active": {
                      transform: "none",
                    },
                  }}
                >
                  <Box
                    component='img'
                    src={supportIcon}
                    mr={isMobile ? "4px" : "6px"}
                    mb={2}
                    w={isMobile ? "12px" : "14px"}
                    h={isMobile ? "12px" : "14px"}
                  />
                  Support
                </Button>
              </Indicator>

              {Number(queryDetails?.QUERY_STATUS_ID) !== 1 && isPreviewMode ? (
                <>
                  {Number(queryDetails?.QUERY_STATUS_ID) === 2 &&
                    queryDetails?.IS_EDITABLE === "Y" && (
                      <Tooltip
                        arrowPosition='side'
                        arrowOffset={5}
                        arrowSize={5}
                        arrowRadius={3}
                        label='Edit'
                        position='bottom-start'
                        withArrow
                      >
                        <ActionIcon
                          color={colors.primary[5]}
                          variant='outline'
                          size={isMobile ? "sm" : "md"}
                          onClick={() => {
                            setIsPreviewMode(false);
                            setIsEdit(true);
                          }}
                          p={4}
                          mt={isMobile ? "1px" : "unset"}
                        >
                          <IconEdit size={16} color='black' />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  <Tooltip
                    arrowPosition='side'
                    arrowOffset={5}
                    arrowSize={5}
                    arrowRadius={3}
                    label='Print'
                    position='bottom-start'
                    withArrow
                  >
                    <ActionIcon
                      color={colors.primary[5]}
                      variant='outline'
                      size={isMobile ? "sm" : "md"}
                      mt={isMobile ? "1px" : "unset"}
                      onClick={handlePrint}
                      loading={printing}
                      disabled={printing}
                      p={4}
                    >
                      <IconPrinter color='black' />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip
                    arrowPosition='side'
                    arrowOffset={5}
                    arrowSize={5}
                    arrowRadius={3}
                    label='Download'
                    position='bottom-start'
                    withArrow
                  >
                    <ActionIcon
                      color={colors.primary[5]}
                      variant='outline'
                      size={isMobile ? "sm" : "md"}
                      mt={isMobile ? "1px" : "unset"}
                      onClick={handleDownload}
                      loading={downloading}
                      disabled={downloading}
                      p={4}
                    >
                      <IconDownload color='black' />
                    </ActionIcon>
                  </Tooltip>
                </>
              ) : (
                <Button
                  size={isMobile ? "compact-xs" : "xs"}
                  variant='outline'
                  disabled={
                    !(diagnosisAcuity?.length > 0) &&
                    !(diagnosisType?.length > 0) &&
                    !(diagnosisPOA?.length > 0)
                  }
                  onClick={() => setOpenResetModal(true)}
                  style={{
                    fontWeight: 500,
                    fontSize: "10px",
                    transition: "none",
                    transform: "none",
                    "&:active": {
                      transform: "none",
                    },
                  }}
                  c={colors.gray[8]}
                  color={colors.primary[5]}
                >
                  Reset
                </Button>
              )}
            </Group>
          </Flex>

          {/* Right Logo */}
          {/* {!isMobile && */}
          <Box
            component='img'
            src={bulwarkLogo}
            style={{
              height: isSmallScreen ? 22 : 28,
              width: "auto",
              flexShrink: 0,
            }}
          />
        </Flex>

        <Divider my={"6px"} />

        {/* Info Panel */}
        <Box mb={"6px"}>
          <InfoPanel
            data={{
              "Admit Date / Discharge Date / LOS": `${admission} / ${discharge} $ ${los}`,
              "Patient Name / MRN": `${queryDetails?.PATIENT_NAME} $ ${queryDetails?.MRN_NO}`,
              "Gender / Age": `${queryDetails?.PATIENT_SEX} / ${queryDetails?.PATIENT_AGE}y`,
              "Visit ID": queryDetails?.VISIT_ID,
              "Facility Name": queryDetails?.FACILITY_NAME,
            }}
            multiLineKeys={[
              "Admit Date / Discharge Date / LOS",
              "Patient Name / MRN",
            ]}
          />
        </Box>

        {/* Main Content */}
        <Stack
          gap='8px'
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "0px 8px 2px 8px",
          }}
          ref={contentRef}
        >
          {/* Greeting Box */}
          <Paper
            py='4px'
            px={"md"}
            bg={colors.gray[2]}
            style={{
              border: `1px solid ${colors.gray[1]}`,
              borderRadius: "4px",
            }}
          >
            <Text size='14px' lh={"20px"} fw={500} c={colors.gray[8]}>
              Dear Dr. {providerDisplayName}
            </Text>
            <Text size='12px' lh={"18px"} fw={400} c={colors.gray[7]}>
              {queryDetails?.FIRST_HEADING_TXT || DUMMY_TEXT.FIRST_HEADING_TXT}
            </Text>
          </Paper>

          {/* Checkbox/Radio Groups */}
          <SimpleGrid
            cols={{
              base: 1,
              xs: Math.min(diagnosisGroup.length, 2),
              sm: Math.min(diagnosisGroup.length, 2),
              md: diagnosisGroup?.length,
            }}
            spacing='xs'
          >
            {diagnosisGroup}
          </SimpleGrid>

          {/* Notification Banner */}
          <Box>
            <NotificationBanner
              message={
                queryDetails?.SEC_HEADING_TXT || DUMMY_TEXT.SEC_HEADING_TXT
              }
              bgColor={colors.warning[0]}
              textColor={colors.gray[8]}
              iconColor={colors.warning[4]}
            />
          </Box>

          {/* Clinical Documentation */}
          <Box>
            <ClinicalDocumentation
              sections={documentationData?.sections}
              clinicalNotes={documentationData.clinicalNotes}
            />
          </Box>

          <Box>
            <NotificationBanner
              message={
                queryDetails?.THIRD_HEADING_TXT || DUMMY_TEXT.THIRD_HEADING_TXT
              }
              bgColor={colors.warning[0]}
              textColor={colors.gray[8]}
              iconColor={colors.warning[4]}
            />
          </Box>

          {/* Signature Footer */}
          <Box>
            <SignatureFooter
              signatureFile={signatureFile}
              providerName={providerDisplayName}
              signedValue={signedValue}
              isPreviewMode={isPreviewMode}
              disableSignButton={!isFormValid()}
              handleSubmit={handleSubmit}
              onSignQuery={() => {
                setIsSignatureModalOpen(true);
              }}
              signInDisabled={!(isFormValid() && signatureFile)}
              hideSaveAndPreview={isPreviewMode && isFormValid()}
              hideSubmit={isFormValid() && Number(queryStatus) === 2 && !isEdit}
              signatureInfo={signatureInfo}
              submitLoading={submitLoading}
              saveLoading={saveLoading}
            />
          </Box>

          {/* Disclaimer */}
          <Paper
            px='md'
            py={"4px"}
            bg={colors.gray[2]}
            style={{
              border: `1px solid ${colors.gray[3]}`,
              borderRadius: "4px",
            }}
          >
            <Text size='12px' lh={1.5}>
              <Text span fw={500}>
                Disclaimer:
              </Text>{" "}
              <Text span c={colors.gray[7]} fw={400}>
                {queryDetails?.FINAL_DISCLAIMER_TXT ||
                  DUMMY_TEXT.FINAL_DISCLAIMER_TXT}
              </Text>
            </Text>
          </Paper>
          <Box
            m='4px'
            style={{
              display: "flex",
              flexDirection: "row",
              fontFamily: "Roboto, sans-serif",
              fontSize: "12px",
              fontWeight: 600,
              lineHeight: "22px",
              color: colors.gray[8],
            }}
          >
            <Box component='span' mr='20px'>
              {`Query Sent By: ${queryDetails?.CREATED_BY}`}
            </Box>
            <Box component='span'>
              {`On: ${formatDate(
                queryDetails?.CREATED_DATE,
                DATE_FORMAT.MM_DD_YYYY,
                provider?.timezone_info?.timezone_name
              )} at ${formatTimeWithZoneAbbreviation(
                queryDetails?.CREATED_DATE,
                provider?.timezone_info?.timezone_name,
                provider?.timezone_info?.abbrev_simple
              )}`}
            </Box>
          </Box>
        </Stack>

        <SignatureInputDialog
          initialValue={providerSignatureDefault}
          opened={isSignatureModalOpen}
          onCancel={() => setIsSignatureModalOpen(false)}
          onSubmit={handleSubmitSignature}
        />

        <Modal
          withCloseButton={false}
          opened={isBackModalOpen}
          onClose={() => setBackModalOpen(false)}
          centered
          size='md'
          padding='1rem'
          style={{ borderRadius: "8px" }}
        >
          <Stack gap='sm'>
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <Box
                component='img'
                height={20}
                width={20}
                mb={4}
                src={InfoIcon}
              />
              <Text fw={600} size='lg'>
                Warning!!
              </Text>
            </Box>
            <Stack gap='4px'>
              <Text c={colors.gray[6]} size='16px' fw={600} lh='22px'>
                All changes will be lost.
              </Text>
              <Text c={colors.gray[6]} size='16px' fw={600} lh='22px'>
                Press “Cancel” to stay, or “Process to Home”
              </Text>
            </Stack>

            <Group justify='space-between' mt='lg'>
              <Button
                variant='outline'
                onClick={() => {
                  setBackModalOpen(false);
                }}
                radius='md'
                c={colors.gray[8]}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  navigate(previousRoute || "/");
                  setBackModalOpen(false);
                }}
                radius='md'
                color={colors.primary[5]}
              >
                Proceed to Home
              </Button>
            </Group>
          </Stack>
        </Modal>

        <ResetModal
          isOpen={openResetModal}
          onClose={() => setOpenResetModal(false)}
          onConfirm={handleReset}
        />
      </Paper>
    </Box>
  );
};

export default QueryDetails;
